import { useState, useCallback } from 'react';
import { AnyEditState, EditState, isAnySaving } from '../types/editState';
import { Note, Review } from '../types/items';
import { CareerCard } from '../types/careerCard';

interface UseEditStateResult {
  noteEditState: EditState<Note> | null;
  reviewEditState: EditState<Review> | null;
  cardEditState: EditState<CareerCard> | null;
  setNoteEditState: (state: EditState<Note> | null) => void;
  setReviewEditState: (state: EditState<Review> | null) => void;
  setCardEditState: (state: EditState<CareerCard> | null) => void;
  getCurrentEditState: () => AnyEditState | null;
  clearAllEditStates: () => void;
  hasAnyUnsavedChanges: () => boolean;
  startSaving: () => void;
  finishSaving: (error?: string) => void;
  isCurrentlySaving: () => boolean;
}

export function useEditState(): UseEditStateResult {
  const [noteEditState, setNoteEditState] = useState<EditState<Note> | null>(null);
  const [reviewEditState, setReviewEditState] = useState<EditState<Review> | null>(null);
  const [cardEditState, setCardEditState] = useState<EditState<CareerCard> | null>(null);

  // Get the current active edit state (only one should be active at a time)
  const getCurrentEditState = useCallback((): AnyEditState | null => {
    return noteEditState || reviewEditState || cardEditState;
  }, [noteEditState, reviewEditState, cardEditState]);

  // Clear all edit states
  const clearAllEditStates = useCallback(() => {
    setNoteEditState(null);
    setReviewEditState(null);
    setCardEditState(null);
  }, []);

  // Check if there are any unsaved changes
  const hasAnyUnsavedChanges = useCallback((): boolean => {
    return Boolean(
      noteEditState?.hasUnsavedChanges ||
      reviewEditState?.hasUnsavedChanges ||
      cardEditState?.hasUnsavedChanges
    );
  }, [noteEditState, reviewEditState, cardEditState]);

  // Start saving process
  const startSaving = useCallback(() => {
    const currentState = getCurrentEditState();
    if (!currentState) return;

    if (noteEditState) {
      setNoteEditState({ ...noteEditState, isSaving: true, saveError: undefined });
    } else if (reviewEditState) {
      setReviewEditState({ ...reviewEditState, isSaving: true, saveError: undefined });
    } else if (cardEditState) {
      setCardEditState({ ...cardEditState, isSaving: true, saveError: undefined });
    }
  }, [getCurrentEditState, noteEditState, reviewEditState, cardEditState]);

  // Finish saving process
  const finishSaving = useCallback((error?: string) => {
    const currentState = getCurrentEditState();
    if (!currentState) return;

    if (noteEditState) {
      setNoteEditState({ ...noteEditState, isSaving: false, saveError: error });
    } else if (reviewEditState) {
      setReviewEditState({ ...reviewEditState, isSaving: false, saveError: error });
    } else if (cardEditState) {
      setCardEditState({ ...cardEditState, isSaving: false, saveError: error });
    }
  }, [getCurrentEditState, noteEditState, reviewEditState, cardEditState]);

  // Check if currently saving
  const isCurrentlySaving = useCallback((): boolean => {
    return isAnySaving([noteEditState, reviewEditState, cardEditState]);
  }, [noteEditState, reviewEditState, cardEditState]);

  return {
    noteEditState,
    reviewEditState,
    cardEditState,
    setNoteEditState,
    setReviewEditState,
    setCardEditState,
    getCurrentEditState,
    clearAllEditStates,
    hasAnyUnsavedChanges,
    startSaving,
    finishSaving,
    isCurrentlySaving,
  };
} 